import type { Repository } from '@/shared/Repository'
import type { Status } from '@ttab/elephant-api/repository'

// Workflow statuses shown in the status history, unpublish is a usable status on version -1
export const STATUS_HISTORY_NAMES = [
  'draft',
  'done',
  'approved',
  'usable',
  'withheld',
  'needs_proofreading',
  'print_done'
]

// The repository returns at most this many statuses per page
export const STATUS_HISTORY_PAGE_SIZE = 10

const MAX_PAGES = 20

/**
 * Fetch every status of each given name, paging backwards until the history is exhausted.
 * @returns Statuses per name, newest first.
 */
export async function fetchStatusHistory({
  repository,
  uuid,
  accessToken,
  names = STATUS_HISTORY_NAMES,
  abort
}: {
  repository: Pick<Repository, 'getStatusHistory'>
  uuid: string
  accessToken: string
  names?: string[]
  abort?: AbortSignal
}): Promise<Record<string, Status[]>> {
  const entries = await Promise.all(names.map(async (name) => {
    const statuses = await fetchAllPages({ repository, uuid, accessToken, name, abort })
    return [name, statuses] as const
  }))

  return Object.fromEntries(entries)
}


async function fetchAllPages({ repository, uuid, accessToken, name, abort }: {
  repository: Pick<Repository, 'getStatusHistory'>
  uuid: string
  accessToken: string
  name: string
  abort?: AbortSignal
}): Promise<Status[]> {
  const statuses: Status[] = []
  let before = 0n

  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await repository.getStatusHistory({ uuid, accessToken, name, before, abort })
    const items = result?.statuses ?? []

    statuses.push(...items)

    if (items.length < STATUS_HISTORY_PAGE_SIZE) {
      break
    }

    before = items[items.length - 1].id
  }

  return statuses
}
